import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Card,
  Button,
  Typography,
  Space,
  Alert,
  Steps,
  Input,
  Modal,
  Tag,
  Divider,
  Radio,
  message,
  Tooltip,
  List
} from 'antd';
import {
  LockOutlined,
  MobileOutlined,
  MailOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  CopyOutlined,
  KeyOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import QRCode from 'qrcode';

const { Title, Text, Paragraph } = Typography;

const TwoFactorSettings = observer(() => {
  const { authStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [setupMethod, setSetupMethod] = useState('TOTP');
  const [setupData, setSetupData] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disableModalVisible, setDisableModalVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);

  const status = authStore.twoFAStatus;

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    await authStore.fetch2FAStatus();
  };

  const handleSetup = async () => {
    setLoading(true);
    const result = await authStore.setup2FA(setupMethod);
    setLoading(false);

    if (result.success) {
      setSetupData(result.data);
      if (setupMethod === 'TOTP' && result.data.provisioning_uri) {
        const qrCode = await QRCode.toDataURL(result.data.provisioning_uri);
        setQrCodeDataUrl(qrCode);
      }
      setCurrentStep(1);
    } else {
      message.error(result.error || 'Failed to start setup');
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length < 6) {
      message.error('Please enter a valid code');
      return;
    }

    setLoading(true);
    const result = await authStore.verify2FASetup(verifyCode);
    setLoading(false);

    if (result.success) {
      setBackupCodes(result.data.backup_codes || []);
      setShowBackupCodes(true);
      setCurrentStep(2);
      message.success('2FA enabled successfully!');
    } else {
      message.error(result.error || 'Invalid verification code');
    }
  };

  const handleDisable = async () => {
    if (!password) {
      message.error('Please enter your password');
      return;
    }

    setLoading(true);
    const result = await authStore.disable2FA(password);
    setLoading(false);

    if (result.success) {
      message.success('2FA has been disabled');
      setDisableModalVisible(false);
      setPassword('');
      setCurrentStep(0);
      setSetupData(null);
      setQrCodeDataUrl('');
      setVerifyCode('');
      setBackupCodes([]);
    } else {
      message.error(result.error || 'Failed to disable 2FA');
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!password) {
      message.error('Please enter your password');
      return;
    }

    setLoading(true);
    const result = await authStore.regenerateBackupCodes(password);
    setLoading(false);

    if (result.success) {
      setBackupCodes(result.data.backup_codes || []);
      setShowBackupCodes(true);
      setRegenerateModalVisible(false);
      setPassword('');
      message.success('New backup codes generated!');
    } else {
      message.error(result.error || 'Failed to generate backup codes');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard!');
  };

  const copyAllBackupCodes = () => {
    copyToClipboard(backupCodes.join('\n'));
  };

  const renderSetupSteps = () => (
    <Steps current={currentStep} style={{ marginBottom: 24 }}>
      <Steps.Step title="Choose Method" />
      <Steps.Step title="Verify" />
      <Steps.Step title="Complete" />
    </Steps>
  );

  const renderMethodSelection = () => (
    <div>
      <Paragraph>Choose your preferred 2FA method:</Paragraph>
      <Radio.Group
        value={setupMethod}
        onChange={(e) => setSetupMethod(e.target.value)}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio.Button value="TOTP" style={{ height: 'auto', padding: 16, width: '100%' }}>
            <Space>
              <MobileOutlined style={{ fontSize: 24 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>Authenticator App (Recommended)</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Use Google Authenticator, Authy, or similar apps
                </Text>
              </div>
            </Space>
          </Radio.Button>
          <Radio.Button value="EMAIL" style={{ height: 'auto', padding: 16, width: '100%' }}>
            <Space>
              <MailOutlined style={{ fontSize: 24 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>Email</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Receive a code via email on each login
                </Text>
              </div>
            </Space>
          </Radio.Button>
        </Space>
      </Radio.Group>
      <Button
        type="primary"
        onClick={handleSetup}
        loading={loading}
        style={{ marginTop: 24 }}
        block
      >
        Continue
      </Button>
    </div>
  );

  const renderTOTPSetup = () => (
    <div style={{ textAlign: 'center' }}>
      <Paragraph>
        Scan this QR code with your authenticator app:
      </Paragraph>
      {qrCodeDataUrl && (
        <img src={qrCodeDataUrl} alt="QR Code" style={{ maxWidth: 200, marginBottom: 16 }} />
      )}
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Or enter this secret manually:
      </Paragraph>
      <Space>
        <Text code style={{ fontSize: 12 }}>{setupData?.secret}</Text>
        <Tooltip title="Copy">
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => copyToClipboard(setupData?.secret)}
          />
        </Tooltip>
      </Space>
      <Divider />
      <Paragraph>
        Enter the 6-digit code from your authenticator app:
      </Paragraph>
      <Input
        size="large"
        placeholder="000000"
        value={verifyCode}
        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
        style={{ textAlign: 'center', letterSpacing: 8, fontSize: 24, maxWidth: 200, margin: '0 auto' }}
      />
      <Button
        type="primary"
        onClick={handleVerifySetup}
        loading={loading}
        disabled={verifyCode.length !== 6}
        style={{ marginTop: 24 }}
        block
      >
        Verify and Enable
      </Button>
    </div>
  );

  const renderEmailSetup = () => (
    <div style={{ textAlign: 'center' }}>
      <MailOutlined style={{ fontSize: 48, color: '#FF9A76', marginBottom: 16 }} />
      <Paragraph>
        Email 2FA is ready to be enabled. A verification code will be sent to your email each time you log in.
      </Paragraph>
      <Paragraph type="secondary">
        Your email: <strong>{setupData?.email}</strong>
      </Paragraph>
      <Paragraph>
        Enter any 6-digit code to verify setup (this confirms your authenticator is working):
      </Paragraph>
      <Input
        size="large"
        placeholder="000000"
        value={verifyCode}
        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
        style={{ textAlign: 'center', letterSpacing: 8, fontSize: 24, maxWidth: 200, margin: '0 auto' }}
      />
      <Alert
        message="For email 2FA, enter '123456' to complete setup (test mode)"
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
      <Button
        type="primary"
        onClick={handleVerifySetup}
        loading={loading}
        style={{ marginTop: 24 }}
        block
      >
        Enable Email 2FA
      </Button>
    </div>
  );

  const renderComplete = () => (
    <div style={{ textAlign: 'center' }}>
      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
      <Title level={4}>2FA Enabled Successfully!</Title>
      <Paragraph>
        Your account is now protected with two-factor authentication.
      </Paragraph>
      {backupCodes.length > 0 && (
        <>
          <Alert
            message="Save your backup codes!"
            description="These codes can be used to access your account if you lose your authenticator. Each code can only be used once."
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16, textAlign: 'left' }}
          />
          <Card
            size="small"
            title={
              <Space>
                <KeyOutlined />
                Backup Codes
              </Space>
            }
            extra={
              <Button icon={<CopyOutlined />} size="small" onClick={copyAllBackupCodes}>
                Copy All
              </Button>
            }
          >
            <List
              grid={{ gutter: 8, column: 2 }}
              dataSource={backupCodes}
              renderItem={(code) => (
                <List.Item>
                  <Text code>{code}</Text>
                </List.Item>
              )}
            />
          </Card>
        </>
      )}
      <Button
        type="primary"
        onClick={() => {
          setCurrentStep(0);
          setSetupData(null);
          setQrCodeDataUrl('');
          setVerifyCode('');
          setBackupCodes([]);
          setShowBackupCodes(false);
        }}
        style={{ marginTop: 24 }}
      >
        Done
      </Button>
    </div>
  );

  const renderEnabled = () => (
    <div>
      <Space style={{ marginBottom: 24 }}>
        <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
        <div>
          <Title level={5} style={{ margin: 0 }}>Two-Factor Authentication is Enabled</Title>
          <Text type="secondary">
            Method: {status?.method === 'TOTP' ? 'Authenticator App' : 'Email'}
          </Text>
        </div>
      </Space>

      <Divider />

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Button
          icon={<KeyOutlined />}
          onClick={() => setRegenerateModalVisible(true)}
        >
          Regenerate Backup Codes
          {status?.backup_codes_count > 0 && (
            <Tag style={{ marginLeft: 8 }}>{status?.backup_codes_count} remaining</Tag>
          )}
        </Button>
        <Button
          danger
          onClick={() => setDisableModalVisible(true)}
        >
          Disable 2FA
        </Button>
      </Space>
    </div>
  );

  return (
    <Card
      title={
        <Space>
          <LockOutlined />
          Two-Factor Authentication
        </Space>
      }
      extra={
        status?.enabled ? (
          <Tag color="green">Enabled</Tag>
        ) : (
          <Tag color="orange">Not Enabled</Tag>
        )
      }
    >
      {status?.enabled ? (
        renderEnabled()
      ) : (
        <>
          {renderSetupSteps()}
          {currentStep === 0 && renderMethodSelection()}
          {currentStep === 1 && setupMethod === 'TOTP' && renderTOTPSetup()}
          {currentStep === 1 && setupMethod === 'EMAIL' && renderEmailSetup()}
          {currentStep === 2 && renderComplete()}
        </>
      )}

      {/* Disable Modal */}
      <Modal
        title="Disable Two-Factor Authentication"
        open={disableModalVisible}
        onCancel={() => {
          setDisableModalVisible(false);
          setPassword('');
        }}
        footer={null}
      >
        <Alert
          message="Warning"
          description="Disabling 2FA will make your account less secure. You will need to re-setup 2FA to enable it again."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Paragraph>Enter your password to confirm:</Paragraph>
        <Input.Password
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <Space>
          <Button onClick={() => {
            setDisableModalVisible(false);
            setPassword('');
          }}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleDisable}
            loading={loading}
          >
            Disable 2FA
          </Button>
        </Space>
      </Modal>

      {/* Regenerate Backup Codes Modal */}
      <Modal
        title="Regenerate Backup Codes"
        open={regenerateModalVisible}
        onCancel={() => {
          setRegenerateModalVisible(false);
          setPassword('');
        }}
        footer={null}
      >
        <Alert
          message="Important"
          description="Generating new backup codes will invalidate all your existing backup codes."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Paragraph>Enter your password to confirm:</Paragraph>
        <Input.Password
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <Space>
          <Button onClick={() => {
            setRegenerateModalVisible(false);
            setPassword('');
          }}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleRegenerateBackupCodes}
            loading={loading}
          >
            Generate New Codes
          </Button>
        </Space>
      </Modal>

      {/* Show Backup Codes Modal */}
      <Modal
        title="Your Backup Codes"
        open={showBackupCodes && backupCodes.length > 0 && !status?.enabled}
        onCancel={() => setShowBackupCodes(false)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyAllBackupCodes}>
            Copy All
          </Button>,
          <Button key="done" type="primary" onClick={() => setShowBackupCodes(false)}>
            I've Saved Them
          </Button>
        ]}
      >
        <Alert
          message="Save these codes in a safe place"
          description="You can use these codes to access your account if you lose your authenticator. Each code can only be used once."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <List
          grid={{ gutter: 8, column: 2 }}
          dataSource={backupCodes}
          renderItem={(code) => (
            <List.Item>
              <Text code>{code}</Text>
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  );
});

export default TwoFactorSettings;
